import { API_BASE } from '@/lib/api-url';
import { sessionRequestHeaders } from '@/lib/session-headers';
import { getAnonId } from './identity';
import { getAttribution } from './attribution';

// · Typed analytics event taxonomy.

export type AnalyticsEventName =
  // Auth + onboarding
  | 'workspace.mode_picked'
  | 'workspace.context_set'
  // Integrations
  | 'integration.connect_started'
  | 'integration.connect_completed'
  | 'integration.connect_failed'
  | 'integration.consent_abandoned'
  | 'integration.first_sync_completed'
  // Diagnostics
  | 'diagnostic.created'
  | 'diagnostic.completed'
  | 'diagnostic.failed'
  | 'diagnostic.verdict_signed'
  // Report engagement
  | 'report.opened'
  | 'report.letter_scroll_complete'
  | 'report.pdf_exported'
  | 'report.share_link_created'
  | 'report.dimension_drilled'
  // Ask Zoe
  | 'ask_zoe.opened'
  | 'ask_zoe.message_sent'
  | 'ask_zoe.answer_exported_csv'
  | 'ask_zoe.answer_emailed'
  | 'ask_zoe.answer_charted'
  // Today
  | 'today.viewed'
  | 'today.card_clicked'
  | 'front_door.submitted'
  | 'landing.viewed'
  // Billing.
  | 'billing.paywall_viewed'
  | 'billing.paywall_dismissed'
  | 'billing.checkout_started'
  | 'billing.payment_failed'
  | 'billing.cancelled'
  // Routing
  | 'route.retired_redirect'
  | 'user.identified';

// Strongly-typed payloads per event.
export interface EventPayloads {
  'workspace.mode_picked': { mode: 'investor' | 'operator' };
  'workspace.context_set': { stage: string; sector: string; headcount_band: string; company_url?: string | null };
  'integration.connect_started': { source: string };
  'integration.connect_completed': { source: string; duration_ms: number };
  'integration.connect_failed': { source: string; reason_category: 'oauth' | 'scope' | 'rate_limit' | 'network' | 'unknown' };
  'integration.consent_abandoned': { source: string; stage: 'consent' | 'provider_redirect' };
  'integration.first_sync_completed': { source: string; metric_count: number };
  'diagnostic.created': { diagnostic_id: string; category: string };
  'diagnostic.completed': { diagnostic_id: string; composite_score: number; duration_minutes: number; llm_cost_usd: number };
  'diagnostic.failed': { diagnostic_id: string; stage: string; reason_category: string };
  'diagnostic.verdict_signed': { diagnostic_id: string; composite_score: number; duration_minutes: number };
  'report.opened': { report_id: string; via: 'email' | 'direct' | 'share_link' };
  'report.letter_scroll_complete': { report_id: string };
  'report.pdf_exported': { report_id: string };
  'report.share_link_created': { report_id: string; expires_in_days: number };
  'report.dimension_drilled': { report_id: string; dimension: string };
  'ask_zoe.opened': { context_type: 'deal' | 'portco' | 'workspace'; via: 'cmdk' | 'bar_click' };
  'ask_zoe.message_sent': { context_type: 'deal' | 'portco' | 'workspace'; message_length: number };
  'ask_zoe.answer_exported_csv': { context_type: 'deal' | 'portco' | 'workspace'; shape: 'metrics' | 'dimensions' | 'generic_rows' | 'text_only' };
  'ask_zoe.answer_emailed': { context_type: 'deal' | 'portco' | 'workspace'; shape: 'metrics' | 'dimensions' | 'generic_rows' | 'text_only' };
  'ask_zoe.answer_charted': { context_type: 'deal' | 'portco' | 'workspace'; chart_kind: 'radar' | 'bar' };
  'today.viewed': { card_count: number };
  'today.card_clicked': { card_severity: 'ok' | 'attn' | 'crit' };
  'front_door.submitted': { route: string; has_website: true };
  'landing.viewed': { route: string; source: 'letter_landing' | 'front_door' };
  'billing.paywall_viewed': PaywallAnalyticsContext;
  'billing.paywall_dismissed': PaywallAnalyticsContext;
  'billing.checkout_started': { plan: string } & Partial<PaywallAnalyticsContext>;
  'billing.payment_failed': { plan: string; reason: string };
  'billing.cancelled': { plan: string; tenure_days: number };
  'route.retired_redirect': { from_pattern: string; to_pattern: string };
  'user.identified': { anon_id: string };
}

export interface PaywallAnalyticsContext {
  surface: 'paywall' | 'billing';
  /** The codename route reference, never the target company's real name. */
  case_ref?: string;
  diagnostic_id?: string;
}

export interface AnalyticsClient {
  capture<N extends AnalyticsEventName>(event: N, payload: EventPayloads[N], workspaceId?: string): void;
  identify(userId: string, traits?: Record<string, unknown>): void;
  reset(): void;
}

// Stub client — replaced with the real PostHog client at app boot.
class StubAnalyticsClient implements AnalyticsClient {
  capture<N extends AnalyticsEventName>(event: N, payload: EventPayloads[N], workspaceId?: string): void {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'test') {
      // eslint-disable-next-line no-console
      console.debug('[analytics:stub]', event, { workspaceId, ...payload });
    }
  }
  identify(_userId: string, _traits?: Record<string, unknown>): void {
    // no-op in stub
  }
  reset(): void {
    // no-op
  }
}

// Backend-ingest client — batches events and flushes to POST /api/analytics/events.
interface QueuedEvent {
  event: string;
  payload: Record<string, unknown>;
  workspace_id?: string;
  user_id?: string;
  client_ts: string;
}

/** The per-user "anon→user merge already delivered" marker. */
function markerKey(userId: string): string {
  return `zoe_identified:${userId}`;
}

export class BackendAnalyticsClient implements AnalyticsClient {
  private queue: QueuedEvent[] = [];
  private user_id?: string;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private readonly FLUSH_INTERVAL_MS = 8_000;
  private readonly BATCH_CAP = 50;
  static readonly KEEPALIVE_BODY_LIMIT = 60 * 1024;
  private readonly base_url: string;

  constructor() {
    this.base_url = API_BASE;

    if (typeof window !== 'undefined') {
      this.flushTimer = setInterval(() => this.flush(), this.FLUSH_INTERVAL_MS);
      window.addEventListener('beforeunload', () => this.flush(true));
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flush(true);
      });
    }
  }

  capture<N extends AnalyticsEventName>(event: N, payload: EventPayloads[N], workspaceId?: string): void {
    this.queue.push({
      event,
      payload: { ...getAttribution(), ...(payload as Record<string, unknown>), anon_id: getAnonId() },
      workspace_id: workspaceId,
      user_id: this.user_id,
      client_ts: new Date().toISOString(),
    });
    if (this.queue.length >= this.BATCH_CAP) this.flush();
  }

  identify(userId: string, _traits?: Record<string, unknown>): void {
    const first = this.user_id !== userId;
    this.user_id = userId;
    if (!first) return;

    // Once per anon→user pair, ever.
    try {
      if (localStorage.getItem(markerKey(userId))) return;
    } catch {
      // Storage unavailable — send the merge anyway.
    }
    this.capture('user.identified', { anon_id: getAnonId() }, undefined);
  }

  /** Persists the "already merged" marker for every identify event in an ACCEPTED batch. */
  private commitIdentifyMarkers(batch: QueuedEvent[]): void {
    for (const ev of batch) {
      if (ev.event !== 'user.identified' || !ev.user_id) continue;
      try {
        localStorage.setItem(markerKey(ev.user_id), '1');
      } catch {
        // Storage unavailable — the merge already landed, which is what matters.
      }
    }
  }

  reset(): void {
    this.user_id = undefined;
    this.queue = [];
  }

  /** Splices the next batch off the queue. */
  private takeBatch(maxBytes?: number): QueuedEvent[] {
    if (maxBytes === undefined) return this.queue.splice(0, this.BATCH_CAP);
    let bytes = byteLength('{"events":[]}');
    let count = 0;
    while (count < this.queue.length && count < this.BATCH_CAP) {
      // +1 for the comma joining this event to the previous one.
      const size = byteLength(JSON.stringify(this.queue[count])) + (count > 0 ? 1 : 0);
      if (count > 0 && bytes + size > maxBytes) break;
      bytes += size;
      count += 1;
    }
    return this.queue.splice(0, count);
  }

  /** `synchronous` is the page-hide / unload flush. */
  flush(synchronous = false): void {
    if (this.queue.length === 0) return;
    const batch = this.takeBatch(synchronous ? BackendAnalyticsClient.KEEPALIVE_BODY_LIMIT : undefined);
    const body = JSON.stringify({ events: batch });
    const url = `${this.base_url}/api/analytics/events`;
    const keepalive = byteLength(body) <= BackendAnalyticsClient.KEEPALIVE_BODY_LIMIT;

    // Fire-and-forget on both paths.
    fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...sessionRequestHeaders(),
      },
      body,
      keepalive,
    })
      .then((res) => {
        const retryable = res.status === 429 || res.status >= 500;
        if (retryable && this.queue.length < this.BATCH_CAP * 4) {
          this.queue.unshift(...batch);
          return;
        }
        // Only an accepted batch may claim the merge happened.
        if (res.ok) this.commitIdentifyMarkers(batch);
      })
      .catch(() => {
        // Network failure — re-queue so we try again next tick; cap to avoid runaway.
        if (this.queue.length < this.BATCH_CAP * 4) this.queue.unshift(...batch);
      });
  }

  // Test helper.
  _peek(): QueuedEvent[] {
    return [...this.queue];
  }

  _stop(): void {
    if (this.flushTimer) clearInterval(this.flushTimer);
    this.flushTimer = null;
  }
}

function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

// Default to backend client in the browser; stub elsewhere (SSR/tests).
let _client: AnalyticsClient =
  typeof window === 'undefined' ? new StubAnalyticsClient() : new BackendAnalyticsClient();

/** Replace the stub client with the production client (PostHog adapter) at app boot. */
export function setAnalyticsClient(client: AnalyticsClient): void {
  _client = client;
}

/** Capture an analytics event. */
export function track<N extends AnalyticsEventName>(
  event: N,
  payload: EventPayloads[N],
  workspaceId?: string
): void {
  // Telemetry is never worth breaking the flow it is measuring.
  try {
    _client.capture(event, payload, workspaceId);
  } catch {
    // Analytics must never throw.
  }
}

export function identify(userId: string, traits?: Record<string, unknown>): void {
  try {
    _client.identify(userId, traits);
  } catch {
    // Deliberately swallowed — see comment above.
  }
}

export function resetAnalytics(): void {
  _client.reset();
}

// Exported for tests + manual flush at signout.
export function flushAnalytics(synchronous = false): void {
  const c = _client as unknown as { flush?: (sync: boolean) => void };
  if (typeof c.flush === 'function') c.flush(synchronous);
}

// Exported for tests — get the active client (e.g. to inspect queue).
export function _getClient(): AnalyticsClient {
  return _client;
}
